import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Cell, Pie, PieChart, PieLabelRenderProps, ResponsiveContainer, Tooltip } from 'recharts';
import { API_BASE_URL } from '../../config/api';

interface Slice {
  name: string;
  displayName: string;
  value: number;
  color: string;
}

interface VersionSlice extends Slice {
  releasedAt?: string | null;
  isOther?: boolean;
}

interface FleetStatus {
  registered: number;
  online: number;
  active: number;
  offline: number;
}

const COLORS = ['#2563eb', '#7c3aed', '#0d9488', '#ea580c', '#db2777', '#4f46e5', '#65a30d', '#0369a1'];
const OTHER_COLOR = '#9ca3af';
const STATUS_COLORS: Record<keyof FleetStatus, string> = {
  registered: '#7c3aed',
  online: '#16a34a',
  active: '#2563eb',
  offline: '#9ca3af',
};
const STATUS_ORDER: Array<keyof FleetStatus> = ['registered', 'online', 'active', 'offline'];
const STATUS_LABELS: Record<keyof FleetStatus, string> = {
  registered: 'Registered',
  online: 'Online',
  active: 'Active',
  offline: 'Offline',
};

const formatVersion = (version: string) => {
  if (!version || version === 'Other') return version || 'Unknown';
  return version.match(/^[vV]/) ? version : `v${version}`;
};

const formatReleased = (iso?: string | null) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatVersionLabel = (version: string, releasedAt?: string | null) => {
  const name = formatVersion(version);
  const released = formatReleased(releasedAt);
  return released ? `${name} (${released})` : name;
};

const renderLabel = (props: PieLabelRenderProps) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
  if (!percent || percent < 0.05 || [cx, cy, innerRadius, outerRadius, midAngle].some((value) => typeof value !== 'number')) {
    return null;
  }
  const radius = Number(innerRadius) + (Number(outerRadius) - Number(innerRadius)) * 0.6;
  const x = Number(cx) + radius * Math.cos((-Number(midAngle) * Math.PI) / 180);
  const y = Number(cy) + radius * Math.sin((-Number(midAngle) * Math.PI) / 180);
  return (
    <text x={x} y={y} fill="#fff" textAnchor={x > Number(cx) ? 'start' : 'end'} dominantBaseline="central" fontSize={12} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const FleetPie: React.FC<{
  title: string;
  slices: Slice[];
  empty?: string;
  actions?: React.ReactNode;
  summary?: React.ReactNode;
}> = ({ title, slices, empty, actions, summary }) => {
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  return (
    <section className="store-dash-chart">
      <div className="store-dash-chart-head">
        <h2>{title}</h2>
        {actions}
      </div>
      {total === 0 ? (
        <p className="store-dash-hint">{empty || 'No devices in this view.'}</p>
      ) : (
        <div className="store-dash-fleet">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={slices}
                dataKey="value"
                nameKey="displayName"
                cx="50%"
                cy="50%"
                innerRadius={0}
                outerRadius={108}
                labelLine={false}
                label={renderLabel}
              >
                {slices.map((slice) => (
                  <Cell key={slice.name} fill={slice.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string) => [
                  `${Number(value).toLocaleString()} devices${total ? ` (${((Number(value) / total) * 100).toFixed(1)}%)` : ''}`,
                  name,
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div>
            {summary}
            <div className="store-dash-legend">
              {slices.map((slice) => (
                <div key={slice.name} className="store-dash-legend-item">
                  <i style={{ background: slice.color }} />
                  <span>{slice.displayName}</span>
                  <em>{slice.value.toLocaleString()}</em>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const StoreDashboardFleetView: React.FC = () => {
  const [slices, setSlices] = useState<VersionSlice[]>([]);
  const [statusSlices, setStatusSlices] = useState<Slice[]>([]);
  const [inventorySlices, setInventorySlices] = useState<Slice[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[] | null>(null);
  const [totalDevices, setTotalDevices] = useState(0);
  const [topCount, setTopCount] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/devices/version-metrics`, {
        params: {
          top: topCount,
          ...(selectedModels ? { models: selectedModels.join(',') } : {}),
        },
        withCredentials: true,
      });
      const versions = response.data?.versions || [];
      const nextModels = Array.isArray(response.data.models) ? response.data.models : [];
      const status: FleetStatus = {
        registered: Number(response.data?.status?.registered || 0),
        online: Number(response.data?.status?.online || 0),
        active: Number(response.data?.status?.active || 0),
        offline: Number(response.data?.status?.offline || 0),
      };
      const inventory = Array.isArray(response.data?.inventory) ? response.data.inventory : [];
      setModels(nextModels);
      setSelectedModels((current) => {
        if (!current) return nextModels;
        const allowed = new Set(nextModels);
        const kept = current.filter((model) => allowed.has(model));
        const next = kept.length ? kept : nextModels;
        if (next.length === current.length && next.every((model: string, index: number) => model === current[index])) {
          return current;
        }
        return next;
      });
      setSlices(versions.map((item: { version?: string; count?: number; releasedAt?: string | null }, index: number) => ({
        name: item.version || 'Unknown',
        displayName: formatVersionLabel(item.version || 'Unknown', item.releasedAt),
        value: Number(item.count || 0),
        releasedAt: item.releasedAt || null,
        isOther: item.version === 'Other',
        color: item.version === 'Other' ? OTHER_COLOR : COLORS[index % COLORS.length],
      })));
      setStatusSlices(STATUS_ORDER.map((key) => ({
        name: key,
        displayName: STATUS_LABELS[key],
        value: status[key],
        color: STATUS_COLORS[key],
      })));
      setInventorySlices(inventory.map((item: { model?: string; count?: number }, index: number) => ({
        name: item.model || 'Unknown',
        displayName: item.model || 'Unknown',
        value: Number(item.count || 0),
        color: COLORS[index % COLORS.length],
      })));
      setTotalDevices(Number(response.data?.totalDevices || 0));
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load firmware distribution');
    } finally {
      setLoading(false);
    }
  }, [topCount, selectedModels]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleModel = (model: string) => {
    setSelectedModels((current) => {
      const selected = current || models;
      return selected.includes(model)
        ? selected.filter((item) => item !== model)
        : [...selected, model];
    });
  };

  const mostCommon = slices.find((slice) => !slice.isOther);
  const registeredCount = statusSlices.find((slice) => slice.name === 'registered')?.value || 0;

  return (
    <div className="store-dash">
      <div className="store-dash-toolbar">
        <div>
          <div className="store-dash-kicker">Devices</div>
          <h1>Fleet</h1>
        </div>
      </div>

      {models.length > 0 && (
        <div className="store-dash-filters">
          <span>Models</span>
          {models.map((model) => (
            <label key={model} className="store-dash-check">
              <input
                type="checkbox"
                checked={(selectedModels || models).includes(model)}
                onChange={() => toggleModel(model)}
              />
              {model}
            </label>
          ))}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <>
          <div className="store-dash-fleet-grid">
            <FleetPie
              title="Device Status"
              slices={statusSlices}
              empty="No devices in this view."
              summary={(
                <div className="store-dash-cards store-dash-fleet-cards">
                  <div className="store-dash-card">
                    <span>Devices</span>
                    <strong>{totalDevices.toLocaleString()}</strong>
                  </div>
                  <div className="store-dash-card">
                    <span>Registered</span>
                    <strong>{registeredCount.toLocaleString()}</strong>
                  </div>
                </div>
              )}
            />
            <FleetPie
              title="Inventory"
              slices={inventorySlices}
              empty="No factory-flashed devices waiting for customer setup."
              summary={(
                <div className="store-dash-cards store-dash-fleet-cards">
                  <div className="store-dash-card">
                    <span>Unset devices</span>
                    <strong>{inventorySlices.reduce((sum, slice) => sum + slice.value, 0).toLocaleString()}</strong>
                  </div>
                </div>
              )}
            />
          </div>

          <FleetPie
            title="Firmware distribution"
            slices={slices}
            empty="No firmware data for the selected models."
            actions={(
              <div className="store-dash-ranges">
                {[5, 10].map((count) => (
                  <button
                    key={count}
                    type="button"
                    className={`store-dash-range${topCount === count ? ' is-active' : ''}`}
                    onClick={() => setTopCount(count)}
                  >
                    Top {count}
                  </button>
                ))}
              </div>
            )}
            summary={(
              <div className="store-dash-cards store-dash-fleet-cards">
                <div className="store-dash-card">
                  <span>Devices</span>
                  <strong>{totalDevices.toLocaleString()}</strong>
                </div>
                <div className="store-dash-card">
                  <span>Most common</span>
                  <strong>{mostCommon ? mostCommon.displayName : '—'}</strong>
                </div>
              </div>
            )}
          />
        </>
      )}
    </div>
  );
};

export default StoreDashboardFleetView;
